import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import { getRootDispatcher } from "@/analytics";
import { type HoneycombEnrolledProducts, sendToHoneycomb } from "@/analytics/sendToHoneycomb";
import { getSelectedSessionId } from "@/sdk";
import { useProductOfferStore } from "..";
import type { Enrollment, EnrollmentResponse } from "../types";
import { EnrollmentStatus, SaleProgress } from "../types";
import { getSalesApiClient } from "./api";

const logger = getLogger({
    module: "enrollment",
    supportTeam: "sales",
});

export const createEnrollment = async (enrollment: Enrollment) => {
    const childLogger = logger.child({ productOfferId: enrollment.productOfferId, sessionId: getSelectedSessionId() });
    try {
        childLogger.info("Making enrollment request");
        const result = await getSalesApiClient().post("v2/enrollment", { json: enrollment }).json<EnrollmentResponse>();
        childLogger.info("Finished enrollment request");

        void getRootDispatcher().dispatchBusinessEvent("GetEnrollmentStatus", {
            ProductOfferId: enrollment.productOfferId,
            enrollmentStatus: result.enrollmentStatus,
            StatusOrigin: "ASP",
            productSku: result.productSku,
            confirmationCode: enrollment.confirmationCode,
        });

        const { getProducts, getProductInfo } = useProductOfferStore.getState();
        const enrolledProducts: HoneycombEnrolledProducts[] = getProducts(enrollment.sessionId)
            .filter((p) => p.saleProgress === SaleProgress.Success)
            .map((p) => {
                return {
                    productSku: p.productSku,
                    productOfferId: p.id,
                    enrollmentStatus: p.saleProgress,
                    statusOrigin: p.salesProvider,
                    displayName: getProductInfo(enrollment.sessionId, p?.productSku)?.displayName,
                };
            });
        const productInfo = getProductInfo(enrollment.sessionId, enrollment?.productSku);
        const newEnrollment: HoneycombEnrolledProducts = {
            enrollmentStatus: result.enrollmentStatus === "SUCCESS" ? SaleProgress.Success : SaleProgress.Failure,
            productSku: enrollment.productSku,
            productOfferId: enrollment.productOfferId,
            statusOrigin: "asp", //all products using this enrollment method go through asp currently
            displayName: productInfo?.displayName,
        };
        const honeycombEnrollment: HoneycombEnrolledProducts[] = enrolledProducts.concat([newEnrollment]);
        sendToHoneycomb(enrollment.sessionId, { enrollment: honeycombEnrollment });

        trackFullStoryEvent(result.enrollmentStatus === "SUCCESS" ? "EnrollmentSuccess" : "EnrollmentFailure", {
            productSku: enrollment.productSku,
            productOfferId: enrollment.productOfferId,
            sessionId: enrollment.sessionId,
            partner: enrollment.partner,
            enrollmentStatus: result.enrollmentStatus,
            statusOrigin: "asp",
        });

        return result;
    } catch (err: unknown) {
        void getRootDispatcher().dispatchBusinessEvent("EnrollmentError", {
            Status: "LAMBDA_ERROR",
            ProductOfferId: enrollment.productOfferId,
            Error: err,
            productSku: enrollment.productSku,
        });

        childLogger.error({ err, enrollment }, "enrollment api call failed");
        return { enrollmentStatus: EnrollmentStatus.Error };
    }
};
